<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToCheckboxes'
});
</script>
<template>
	<div :id="id" :flex="flex ? flex : null">
		<span v-if="validateRequired" v-show="false" class="to-validate" :req="validateRequired" :desc="validateDesc" :value="checks.length > 0 ? 'true' : ''"></span>
		<slot></slot>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, inject, onBeforeUnmount, watch, provide } from 'vue'



export interface ToCheckboxesProps {
	// 表单校验
	desc?: string
	id?: string
	required?: boolean
	value?: string
	modelValue: string
	beforeChange?(): boolean | Promise<boolean>
	flex?: boolean
}

const props = withDefaults(defineProps<ToCheckboxesProps>(), {
	desc: '',
	id: '',
	required: false,
	value: '',
	modelValue: '',
	flex: false,
	beforeChange: () => true
})

const emit = defineEmits(['change', 'update:modelValue'])

// 注入表单相关
const ToFormItem = inject('ToFormItem', '')

// 响应式数据
const value = ref<string>(props.modelValue)
const maxId = ref<number>(-1)
const options = ref<any[]>([])
const checks = ref<any[]>([])
const removeItems = ref<any[]>([])
const timer = ref<NodeJS.Timeout | null>(null)

// 计算属性
const validateRequired = computed(() => {
	if (props.required) {
		return 'true'
	} else if (ToFormItem && (ToFormItem as any).required) {
		return 'true'
	} else {
		return 'false'
	}
})

const validateDesc = computed(() => {
	if (props.desc) {
		return props.desc
	} else if (ToFormItem && (ToFormItem as any).label) {
		return (ToFormItem as any).label
	} else {
		return ''
	}
})

// 方法
const change = (checked: boolean, value: string, label: string) => {
	let arr: string[] = []
	checks.value = options.value.filter(item => item.checked)
	checks.value.forEach(el => {
		arr.push(el.value)
	})
	emit('change', arr.join(','), checked, value, label)
	emit('update:modelValue', arr.join(','))
}

const clear = () => {
	emit('change', '', false)
	emit('update:modelValue', '')
}

const all = () => {
	let arr: string[] = []
	options.value.forEach(el => {
		if (!el.disabled) {
			arr.push(el.value)
		}
	})
	emit('change', arr.join(','))
	emit('update:modelValue', arr.join(','))
}

const observe = () => {
	let arr: string[] = []
	options.value
		.filter(item => !item.checked)
		.forEach(el => {
			arr.push(el.value)
		})
	emit('change', arr.join(','))
	emit('update:modelValue', arr.join(','))
}

const removeItemValue = (itemOldValue: string) => {
	if (timer.value) {
		clearTimeout(timer.value)
	}
	removeItems.value.push(itemOldValue)

	timer.value = setTimeout(() => {
		let arr = props.value.split(',')
		arr = arr.filter(item => !removeItems.value.includes(item))
		options.value = options.value.filter(item => !removeItems.value.includes(item.value))
		emit('change', arr.join(','))
		emit('update:modelValue', arr.join(','))
	}, 1)
}

const pushOptions = (option: { value: any; checked: boolean }) => {
	const selectedValues = value.value.split(',').filter(v => v !== '')
	if (selectedValues.includes(String(option.value))) {
		option.checked = true
	}
	options.value.push(option)
	checks.value = options.value.filter(item => item.checked)
}

// 清理定时器
onBeforeUnmount(() => {
	if (timer.value) {
		clearTimeout(timer.value)
	}
})

// 监听value变化，同步checkbox状态
watch(
	() => props.modelValue,
	newVal => {
		if (newVal !== undefined) {
			const selectedValues = newVal.split(',').filter(v => v !== '')
			options.value.forEach(option => {
				option.checked = selectedValues.includes(String(option.value))
			})
			checks.value = options.value.filter(item => item.checked)
		}
	}
)

export interface ProvideType {
	value: import('vue').ComputedRef<string>
	props: ToCheckboxesProps
	pushOptions: (option: { id?: number; value: any; label?: string; checked: boolean; disabled?: boolean }) => void
	options: import('vue').Ref<
		Array<{
			id?: number
			value: any
			label?: string
			checked: boolean
			disabled?: boolean
		}>
	>
	removeItemValue: (itemOldValue: any) => void
	checks: import('vue').Ref<any[]>
	change: (checked: boolean, value: any, label: string) => void
	maxId: import('vue').Ref<number>
}

provide<ProvideType>('ToCheckboxes', {
	value: computed(() => props.modelValue),
	props,
	pushOptions,
	options,
	removeItemValue,
	checks,
	change,
	maxId
})

// 暴露方法和数据给子组件
defineExpose({
	maxId,
	options,
	checks,
	value: computed(() => props.modelValue),
	change,
	clear,
	all,
	observe,
	removeItemValue,
	pushOptions
})
</script>
